import {toastr} from 'react-redux-toastr';
import {initialize, change, reset, formValueSelector} from 'redux-form';
import {validateEmail} from 'shared/validators';
import {
  navigateTo,
  getJsonAsync,
  putJsonAsync,
  postJsonAsync
} from 'shared/actions';
import {
  setErrorList
} from 'shared/error/actionTypes';
import {
  displayVirtualEventSubmitting,
  setVirtualEventDetailsInState,
  setVirtualEntriesInState,
  setAvailableBowlersInState,
  setAvailableTeamsInState,
  calculateSelectedBalance,
  setVirtualEntrySelected,
  displayVirtualEntryDetailPaymentModal,
  setSelectedBalance,
  setRefundTypeForAdminVirtualEntryDetail,
  setIsNewCaptainOnSearch,
  hideVirtualEntryDetailChangeCaptainModal,
  setCreditPurchaseTransactions
} from './actionTypes';

export const handleCaptainDetailsLinkClick = () => (dispatch, getState) => {
  const captainId = getState().admin.virtualEventDetail.captainId;

  dispatch(navigateTo(`admin/captain/${captainId}`));
};

export const handleSaveVirtualEventEntriesBowlers = virtualEventId => (dispatch, getState) => {
  const state = getState();
  const captainId = state.admin.virtualEventDetail.captainId;
  const virtualEntries = state.admin.virtualEventDetail.virtualEntries
    .filter(virtualEntry => !virtualEntry.isCancelled);

  dispatch(displayVirtualEventSubmitting(true));
  dispatch(putJsonAsync(`admin/virtualevent/${virtualEventId}/captain/${captainId}/virtualentry`, {virtualEntries}))
  .then(() => {
    dispatch(displayVirtualEventSubmitting(false));
    toastr.success('Saved!', 'Virtual entries saved successfully.');
  })
  .catch(() => dispatch(displayVirtualEventSubmitting(false)));
};

export const getVirtualEventDetailInfo = (captainId, virtualEventId, paymentId) => dispatch => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`admin/virtualevent/${virtualEventId}/captain/${captainId}`))
    .then(response => {
      dispatch(setVirtualEventDetailsInState(response));
      resolve(response);
    })
  .catch(() => reject());
  
  if (paymentId) {
    dispatch(virtualEventPayment(captainId, virtualEventId, paymentId));
  }
});

const virtualEventPayment = (captainId, virtualEventId, paymentId) => dispatch => {
  dispatch(postJsonAsync('admin/virtualevent/payment/credit', {
    BypassWebHooks: localStorage.bypassWebHooks,
    captainId: captainId,
    paymentId: paymentId,
    virtualEventId: virtualEventId
  }))
  .then(response => {
    if (response) toastr.success('Payment Processed!', 'Selected entries were paid successfully.');
    dispatch(getVirtualEventDetailInfo(captainId, virtualEventId));
    dispatch(retrieveVirtualEntries(captainId, virtualEventId));
    dispatch(setSelectedBalance(0));
  });

  if (localStorage.bypassWebHooks)
    delete localStorage.bypassWebHooks;
};

export const retrieveAvailableBowlers = (captainId, virtualEventId) => dispatch => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`admin/virtualevent/${virtualEventId}/captain/${captainId}/bowler`))
  .then(bowlers => {
    dispatch(setAvailableTeamsInState(null));
    dispatch(setAvailableBowlersInState(bowlers));
    resolve(bowlers);
  })
  .catch(() => reject());
});

export const retrieveAvailableTeams = (captainId, virtualEventId) => dispatch => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`admin/virtualevent/${virtualEventId}/captain/${captainId}/teams`))
  .then(teams => {
    dispatch(setAvailableBowlersInState(null));
    dispatch(setAvailableTeamsInState(teams));
    resolve(teams);
  })
  .catch(() => reject());
});

export const retrieveVirtualEntries = (captainId, virtualEventId) => (dispatch, getState) => {
  const eventOwner = getState().admin.virtualEventDetail.eventOwner;

  const promise = eventOwner._value === 'Team Entry'
  ? retrieveAvailableTeams(captainId, virtualEventId)
  : retrieveAvailableBowlers(captainId, virtualEventId);

  dispatch(promise)
  .then(() => dispatch(getJsonAsync(`admin/virtualevent/${virtualEventId}/captain/${captainId}/virtualentry`))
    .then(virtualEntries => dispatch(setVirtualEntriesInState(virtualEntries))));
};

export const handleVirtualEntrySelectChecked = (virtualEntryId) => dispatch => {
  dispatch(setVirtualEntrySelected(virtualEntryId));
  dispatch(calculateSelectedBalance());
};

export const handlePayBalanceClick = () => (dispatch, getState) => {
  const state = getState();
  const captainId = state.admin.virtualEventDetail.captainId;
  const tournament = state.admin.virtualEventDetail.tournament;
  const virtualEventId = state.admin.virtualEventDetail.virtualEventId;

  const virtualEntries = state.admin.virtualEventDetail.virtualEntries
    .filter(virtualEntry => virtualEntry.selected)
    .map(virtualEntry => virtualEntry.id);

  let body = {
    captainId: captainId,
    source: 'virtualEventDetail',
    tournamentId: tournament.id,
    virtualEntries: virtualEntries,
    virtualEventId: virtualEventId
  };

  dispatch(postJsonAsync(`admin/virtualEvent/createSession`, body))
  .then(response => {
    const publishableKey = tournament.stripePublishableKey;
    const stripe = window.Stripe(publishableKey);
    const sessionId = response.paymentId;

    stripe.redirectToCheckout({
      sessionId
    });
  });
};

export const handlePayBalanceOtherClick = () => (dispatch, getState) => {
  const amount = getState().admin.virtualEventDetail.selectedBalance;

  dispatch(initialize('adminVirtualEntryPaymentForm', {
    amount: amount
  }));
  dispatch(displayVirtualEntryDetailPaymentModal(true, 'Payment'));
};

export const handlePayBalanceOtherCancelClick = () => dispatch => {
  dispatch(setErrorList([]));
  dispatch(displayVirtualEntryDetailPaymentModal(false));
  dispatch(reset('adminVirtualEntryPaymentForm'));
};

export const handlePaymentModalSubmitClick = data => (dispatch, getState) => new Promise((resolve, reject) => {
  const state = getState().admin.virtualEventDetail;
  const captainId = state.captainId;
  const virtualEventId = state.virtualEventId;
  const tournament = state.tournament;
  const refundType = state.refundType;
  const virtualEntries = state.virtualEntries
    .filter(virtualEntry => virtualEntry.selected)
    .map(virtualEntry => virtualEntry.id);

  if (data.stripeTransactionId) data.stripeTransactionId = data.stripeTransactionId.trim();
  const body = {
    ...data,
    tournamentId: tournament.id,
    captainId,
    virtualEntries
  };

  data = {
    ...data,
    refundOnly: refundType === 'refundOnly'
  };

  if(state.paymentModalType === 'Payment') {
    dispatch(postJsonAsync(`admin/virtualEvent/payment/other`, body))
    .then(() => {
      toastr.success('Saved!', 'Payment was successfully saved.');
      dispatch(setErrorList([]));
      dispatch(displayVirtualEntryDetailPaymentModal(false));
      dispatch(reset('adminVirtualEntryPaymentForm'));
      dispatch(getVirtualEventDetailInfo(captainId, virtualEventId));
      dispatch(retrieveVirtualEntries(captainId, virtualEventId));
      resolve();
    })
    .catch(() => reject());
  } else if(state.paymentModalType === 'Refund') {
    dispatch(postJsonAsync('admin/virtualevent/virtualEntry/cancel', data))
    .then(() => {
      toastr.success('Canceled!', 'Selected Virtual Entry was successfully canceled');
      dispatch(setErrorList([]));
      dispatch(displayVirtualEntryDetailPaymentModal(false));
      dispatch(getVirtualEventDetailInfo(captainId, virtualEventId));
      dispatch(retrieveVirtualEntries(captainId, virtualEventId));
      resolve();
    })
    .catch(() => reject());
  }
});

export const handleVirtualEntryCancelClick = (virtualEntryId) => (dispatch) => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`admin/entry/${virtualEntryId}/payment/creditLog`)) 
  .then(response => {
    dispatch(setCreditPurchaseTransactions(response.payments));
    dispatch(initialize('adminVirtualEntryPaymentForm', {virtualEntryId}));
    dispatch(displayVirtualEntryDetailPaymentModal(true, 'Refund'));
    dispatch(setRefundTypeForAdminVirtualEntryDetail('refundWithCancellation'));
    resolve();
  })
  .catch(error => reject(error));
});

export const handleVirtualEntryReactivateClick = (virtualEntryId) => (dispatch, getState) => {
  const state = getState().admin.virtualEventDetail;
  const captainId = state.captainId;
  const virtualEventId = state.virtualEventId;

  dispatch(postJsonAsync('admin/virtualEvent/virtualEntry/reactivate', {virtualEntryId}))
  .then(() => {
    toastr.success('Reactivated!', 'Selected Virtual Entry has been reactivated');
    dispatch(getVirtualEventDetailInfo(captainId, virtualEventId));
    dispatch(retrieveVirtualEntries(captainId, virtualEventId));
  })
  .catch(() => {
    toastr.error('Error','There was an error reactivating the selected virtual entry.');
  });
};

export const handleVirtualEntryRefundClick = (virtualEntryId) => (dispatch) => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`admin/entry/${virtualEntryId}/payment/creditLog`)) 
  .then(response => {
    dispatch(setCreditPurchaseTransactions(response.payments));
    dispatch(initialize('adminVirtualEntryPaymentForm', {virtualEntryId}));
    dispatch(displayVirtualEntryDetailPaymentModal(true, 'Refund'));
    dispatch(setRefundTypeForAdminVirtualEntryDetail('refundOnly'));
    resolve();
  })
  .catch(error => reject(error));
});

export const handleVirtualEntryChangeCaptainSearchClick = () => (dispatch, getState) => {
  const selector = formValueSelector('CaptainVirtualEntryChangeForm');
  const captainEmail = selector(getState(), 'captainEmail');
  const virtualEventDetail = getState().admin.virtualEventDetail;
  const virtualEventId = virtualEventDetail.virtualEventId;
  const selectedVirtualEntries = virtualEventDetail.selectedVirtualEntries;

  dispatch(getJsonAsync(`admin/captain/${captainEmail}/email/${virtualEventId}`))
  .then(response => {
    if(response){
      dispatch(change('CaptainVirtualEntryChangeForm', 'captainFirstName', response.captainFirstName));
      dispatch(change('CaptainVirtualEntryChangeForm', 'captainLastName', response.captainLastName));
      if (response.purchasableVirtualEntryCount < selectedVirtualEntries.length)
      {
        dispatch(setErrorList([`The selected captain can support at most ${response.purchasableVirtualEntryCount} virtual 
        entr${response.purchasableVirtualEntryCount !== 1 ? 'ies' : 'y'} and you have selected ${selectedVirtualEntries.length} 
        virtual entr${selectedVirtualEntries.length !== 1 ? 'ies' : 'y'}. Please either select a different captain or fewer 
        virtual entries.`]));
      }
      else
      {
        dispatch(setErrorList([]));
      }
      dispatch(setIsNewCaptainOnSearch(false));
    }
    else{
      dispatch(change('CaptainVirtualEntryChangeForm', 'captainFirstName', ''));
      dispatch(change('CaptainVirtualEntryChangeForm', 'captainLastName', ''));
      dispatch(setIsNewCaptainOnSearch(true));
    }
  })
  .catch(() => {
    toastr.error('Error getting captain detail!', 'There was an error getting captain detail.');
  });
};

export const handleVirtualEntryChangeCaptainCancelClick = () => dispatch => {
  dispatch(hideVirtualEntryDetailChangeCaptainModal());
  dispatch(reset('CaptainVirtualEntryChangeForm'));
  dispatch(setErrorList([]));
};

export const validateVirtualEntryCaptainChangeForm = data => {
  const errors = {};

  if(!data.captainEmail || !validateEmail(data.captainEmail)) {
    errors.captainEmail = 'Please provide a valid email.';
  }

  if (!data.captainFirstName) {
    errors.captainFirstName = 'Please search for the captain before updating.';
  }

  return errors;
};

export const handleChangeVirtualEntriesCaptainFormSave = data => (dispatch, getState) => new Promise(() => {
  const virtualEventDetail = getState().admin.virtualEventDetail;
  const virtualEntries = virtualEventDetail.selectedVirtualEntries.map(entry => entry.id);

  const entryData = {
    ...data,
    captainId: virtualEventDetail.captainId,
    virtualEventId: virtualEventDetail.virtualEventId,
    virtualEntries: virtualEntries
  };

  dispatch(handleChangeVirtualEntryCaptainFormSave(entryData));
});

export const handleChangeVirtualEntryCaptainFormSave = data => (dispatch) => new Promise(() => {
  const entryId = data.virtualEntries.shift();

  const entryData = {
    ...data,
    entries: data.virtualEntries
  };

  const request = {
    virtualEntryId: entryId,
    firstName: data.captainFirstName,
    lastName: data.captainLastName,
    email: data.captainEmail
  };

  dispatch(changeVirtualEntryCaptain(request)).then(() => {
    if (data.virtualEntries.length === 0) {
      dispatch(loadCaptainVirtualEntries(data.captainId, data.virtualEventId));
      dispatch(handleVirtualEntryChangeCaptainCancelClick());
    }
    else {
      // Next Entry 
      dispatch(handleChangeVirtualEntryCaptainFormSave(entryData));
    }
  });
});

export const loadCaptainVirtualEntries = (captainId, virtualEventId) => dispatch => {
  dispatch(getJsonAsync(`admin/virtualevent/${virtualEventId}/captain/${captainId}/virtualentry`))
  .then(response => {
    dispatch(setVirtualEntriesInState(response));
    dispatch(calculateSelectedBalance());
  });
};

export const changeVirtualEntryCaptain = request => (dispatch) => new Promise((resolve) => {
  dispatch(postJsonAsync(`admin/captain/change/virtualentry`, request))
  .then(() => {
    dispatch(hideVirtualEntryDetailChangeCaptainModal());
    toastr.success('Captain Changed!', 'Captain was changed.');
    resolve();
  })
  .catch(() => {
    toastr.error('Captain Not Changed!', 'There was an error changing the captain.');
    resolve();
  });
});



// WEBPACK FOOTER //
// ./src/features/admin/virtualEventDetail/actions.js